import { useState } from "react";
import "@/App.css";

const WEB_APP_URL = process.env.REACT_APP_WEB_APP_URL || "https://app.heirloomly.app";

function App() {
  const [activeTab, setActiveTab] = useState("login");
  const [activeFaq, setActiveFaq] = useState(null);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);

  const handleCTAClick = () => {
    window.location.href = WEB_APP_URL;
  };

  const toggleFaq = (index) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    window.location.href = WEB_APP_URL;
  };

  return (
    <div className="App">
      <main>
        {/* Header with Logo and CTA */}
        <header className="site-header">
          <div className="container">
            <div className="header-content">
              <img 
                src="https://customer-assets.emergentagent.com/job_site-integration-5/artifacts/0toofmmf_69023EB7-ACA3-4F01-BED5-ECD433CDF72E.png"
                alt="Heirloomly"
                className="header-logo"
              />
              <button 
                className="btn-primary header-cta"
                onClick={handleCTAClick}
                data-testid="header-get-started-btn"
              >
                Get started
              </button>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <section className="hero">
          <div className="container">
            <div className="hero-grid">
              <div className="hero-content">
                <h1 className="hero-large">A peaceful way to organize what matters most</h1>
                <p className="hero-medium space-top-lg">
                  Create your private space to organize what matters most—at your own pace.
                </p>
                <p className="body-small space-top-md" style={{ color: 'var(--text-light)' }}>
                  No pressure. Take your time.
                </p>
              </div>
              <div className="hero-image">
                <img
                  alt="White multi-generational family together"
                  loading="eager"
                  src="https://images.unsplash.com/photo-1767411354952-c1e751238ac0?w=800&q=80"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section id="features" className="features section-padding">
          <div className="container">
            <div className="features-header">
              <h2 className="heading-1">Making the complex feel simple</h2>
              <p className="body-large space-top-md" style={{ maxWidth: '700px' }}>
                We designed Heirloomly to feel like a calm companion, not another task on your list.
              </p>
            </div>
            <div className="features-grid space-top-xl">
              <div className="feature-card">
                <div className="feature-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path>
                    <path d="M14 2v4a2 2 0 0 0 2 2h4"></path>
                    <path d="M10 9H8"></path>
                    <path d="M16 13H8"></path>
                    <path d="M16 17H8"></path>
                  </svg>
                </div>
                <h3 className="heading-3 space-top-md">Keep everything in one place</h3>
                <p className="body-regular space-top-sm">
                  Documents, passwords, financial accounts, and personal wishes—organized simply, without the overwhelm.
                </p>
              </div>
              <div className="feature-card">
                <div className="feature-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"></path>
                  </svg>
                </div>
                <h3 className="heading-3 space-top-md">Share what matters, when you're ready</h3>
                <p className="body-regular space-top-sm">
                  Decide what to pass along and when. There's no rush, no deadline. Just clarity on your terms.
                </p>
              </div>
              <div className="feature-card">
                <div className="feature-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <rect width="18" height="11" x="3" y="11" rx="2" ry="2"></rect>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                  </svg>
                </div>
                <h3 className="heading-3 space-top-md">Private and secure</h3>
                <p className="body-regular space-top-sm">
                  Your information stays protected and private. Only you decide who sees what, and when.
                </p>
              </div>
              <div className="feature-card">
                <div className="feature-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <circle cx="12" cy="12" r="10"></circle>
                    <polyline points="12 6 12 12 16 14"></polyline>
                  </svg>
                </div>
                <h3 className="heading-3 space-top-md">Go at your own pace</h3>
                <p className="body-regular space-top-sm">
                  Start small. Add more over time. This isn't a checklist—it's a living space for what's important to you.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Trust Section */}
        <section className="trust">
          <div className="container">
            <div className="trust-grid">
              <div className="trust-image">
                <img
                  alt="Black multi-generational family at home"
                  loading="lazy"
                  src="https://images.unsplash.com/photo-1609253932702-796cbf3d3171?w=800&q=80"
                />
              </div>
              <div className="trust-content">
                <h2 className="heading-1">Built on trust, not pressure</h2>
                <div className="trust-points space-top-xl">
                  <div className="trust-point">
                    <h3 className="heading-3">This is not a legal service</h3>
                    <p className="body-regular space-top-sm">
                      Heirloomly helps you organize and document. It doesn't replace legal advice or formal estate planning—it complements it.
                    </p>
                  </div>
                  <div className="trust-point space-top-lg">
                    <h3 className="heading-3">No pressure to finish</h3>
                    <p className="body-regular space-top-sm">
                      You don't need to complete everything at once. Add what feels right, when it feels right. Come back anytime.
                    </p>
                  </div>
                  <div className="trust-point space-top-lg">
                    <h3 className="heading-3">Your information is yours</h3>
                    <p className="body-regular space-top-sm">
                      We use industry-standard encryption to keep your data secure. You control who has access, and you can delete everything whenever you want.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="testimonials section-padding">
          <div className="container">
            <div className="testimonials-header">
              <h2 className="heading-1">Early reflections</h2>
            </div>
            <div className="testimonials-grid space-top-xl">
              <div className="testimonial-card">
                <p className="testimonial-quote heading-3">"This feels calmer than I expected"</p>
                <p className="testimonial-text body-regular space-top-md">
                  I kept putting this off because I thought it would be overwhelming. But it's not. I can add things as I think of them.
                </p>
                <p className="testimonial-attribution body-small space-top-md">— Early access user</p>
              </div>
              <div className="testimonial-card">
                <p className="testimonial-quote heading-3">"It doesn't feel like a deadline"</p>
                <p className="testimonial-text body-regular space-top-md">
                  There's no pressure to finish everything right away. It feels more like care than planning.
                </p>
                <p className="testimonial-attribution body-small space-top-md">— Beta participant</p>
              </div>
              <div className="testimonial-card">
                <p className="testimonial-quote heading-3">"I like that I'm in control"</p>
                <p className="testimonial-text body-regular space-top-md">
                  I decide what to share and when. That makes all the difference.
                </p>
                <p className="testimonial-attribution body-small space-top-md">— Early supporter</p>
              </div>
              <div className="testimonial-card">
                <p className="testimonial-quote heading-3">"It feels respectful of where I am"</p>
                <p className="testimonial-text body-regular space-top-md">
                  This doesn't feel like a legal form or a to-do list. It feels thoughtful.
                </p>
                <p className="testimonial-attribution body-small space-top-md">— Beta participant</p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section id="faq" className="faq section-padding">
          <div className="container">
            <div className="faq-header">
              <h2 className="heading-1">Questions you might have</h2>
              <p className="body-large space-top-md" style={{ maxWidth: '700px' }}>
                We know this topic can feel heavy. Here are some answers that might help.
              </p>
            </div>
            <div className="faq-list space-top-xl">
              {[
                {
                  question: "Is this like writing a will?",
                  answer: "Not exactly. Heirloomly is a place to organize important information—account details, wishes, memories, and instructions. It's meant to work alongside legal documents like wills and trusts, not replace them."
                },
                {
                  question: "Do I need to finish everything right away?",
                  answer: "Not at all. Most people start with just a few things and add more over time. There's no deadline or pressure. You can save your progress and come back whenever you're ready."
                },
                {
                  question: "Who can see my information?",
                  answer: "Only you, unless you explicitly share access with someone. You're in complete control of who sees what, and you can change permissions or revoke access at any time."
                },
                {
                  question: "Is my information secure?",
                  answer: "Yes. We use bank-level encryption to protect your data. Your information is stored securely, and only you have the keys to access it. We take privacy and security very seriously."
                },
                {
                  question: "What if I'm not ready to think about this yet?",
                  answer: "That's completely okay. This isn't urgent. You can start whenever it feels right for you. There's no timeline you need to follow—simply begin when you're ready."
                },
                {
                  question: "Can I delete my information later?",
                  answer: "Absolutely. You own your data, and you can download it or delete it entirely at any time. No questions asked."
                }
              ].map((faq, index) => (
                <div key={index} className={`faq-item ${activeFaq === index ? 'active' : ''}`}>
                  <button className="faq-question" onClick={() => toggleFaq(index)}>
                    <span className="heading-3">{faq.question}</span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="faq-icon"
                      aria-hidden="true"
                    >
                      <path d="m6 9 6 6 6-6"></path>
                    </svg>
                  </button>
                  <div className="faq-answer">
                    <p className="body-regular">{faq.answer}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Auth/Signup Section */}
        <section id="signup" className="auth section-padding">
          <div className="container">
            <div className="auth-content">
              <h2 className="heading-1">Begin when you're ready</h2>
              <p className="body-large space-top-md">
                Create your private space to organize what matters most—at your own pace.
              </p>
              <div className="auth-tabs space-top-xl">
                <button
                  className={`auth-tab ${activeTab === 'login' ? 'active' : ''}`}
                  onClick={handleCTAClick}
                  data-testid="cta-login-btn"
                >
                  Log in
                </button>
                <button
                  className={`auth-tab ${activeTab === 'create' ? 'active' : ''}`}
                  onClick={handleCTAClick}
                  data-testid="cta-create-account-btn"
                >
                  Create account
                </button>
              </div>
              <form className="auth-form space-top-lg" onSubmit={handleSubmit}>
                <p className="body-regular" style={{ color: 'var(--text-secondary)', marginBottom: '32px' }}>
                  This is your space. You can begin now or come back anytime.
                </p>
                <div className="form-field">
                  <label htmlFor="email" className="body-small">
                    Email address
                  </label>
                  <input
                    id="email"
                    placeholder="your@email.com"
                    className="auth-input"
                    type="email"
                  />
                </div>
                <div className="form-field">
                  <label htmlFor="password" className="body-small">
                    Password
                  </label>
                  <input
                    id="password"
                    placeholder="Enter your password"
                    className="auth-input"
                    type="password"
                  />
                </div>
                <button type="submit" className="btn-primary btn-full-width">
                  {activeTab === 'login' ? 'Log in' : 'Create account'}
                </button>
                <p className="body-small space-top-md" style={{ color: 'var(--text-light)', textAlign: 'center' }}>
                  We respect your privacy. Your information belongs to you.
                </p>
              </form>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <h4 className="body-regular" style={{ fontWeight: 500, marginBottom: '16px' }}>About Heirloomly</h4>
              <p className="body-regular" style={{ color: 'var(--text-secondary)', lineHeight: '1.7' }}>
                Heirloomly is a calm, secure space designed to help people organize important information, memories, and wishes—at their own pace, and on their own terms.
              </p>
            </div>
            <div className="footer-section">
              <h4 className="body-regular" style={{ fontWeight: 500, marginBottom: '16px' }}>Contact</h4>
              <p className="body-regular" style={{ color: 'var(--text-secondary)', lineHeight: '1.7' }}>
                Questions or thoughts? You can reach us anytime at{' '}
                <a href="mailto:support@heirloomly.app" style={{ color: 'var(--text-primary)', textDecoration: 'underline' }}>
                  support@heirloomly.app
                </a>
              </p>
            </div>
            <div className="footer-section">
              <h4 className="body-regular" style={{ fontWeight: 500, marginBottom: '16px' }}>Privacy</h4>
              <p className="body-regular" style={{ color: 'var(--text-secondary)', lineHeight: '1.7' }}>
                Your information belongs to you. We don't sell it, share it, or use it beyond what's needed to provide the service.{' '}
                <button 
                  onClick={() => setShowPrivacyModal(true)}
                  style={{ 
                    color: 'var(--text-primary)', 
                    textDecoration: 'underline',
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    font: 'inherit'
                  }}
                >
                  Read our full Privacy Policy
                </button>
              </p>
            </div>
            <div className="footer-section">
              <h4 className="body-regular" style={{ fontWeight: 500, marginBottom: '16px' }}>Terms</h4>
              <p className="body-regular" style={{ color: 'var(--text-secondary)', lineHeight: '1.7' }}>
                Heirloomly is an organizational and documentation tool. It is not a legal, financial, or medical service and does not replace professional advice.{' '}
                <button 
                  onClick={() => setShowTermsModal(true)}
                  style={{ 
                    color: 'var(--text-primary)', 
                    textDecoration: 'underline',
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    font: 'inherit'
                  }}
                >
                  Read our full Terms of Use
                </button>
              </p>
            </div>
          </div>
          <div className="footer-bottom">
            <p className="body-regular" style={{ color: 'var(--text-secondary)', fontStyle: 'italic', marginBottom: '16px' }}>
              Your information is private. Your pace is respected.
            </p>
            <p className="body-small" style={{ color: 'var(--text-light)' }}>
              © {new Date().getFullYear()} Heirloomly. All rights reserved. Built with care.
            </p>
          </div>
        </div>
      </footer>

      {/* Terms Modal */}
      {showTermsModal && (
        <div className="legal-modal-overlay" onClick={() => setShowTermsModal(false)}>
          <div className="legal-modal" onClick={(e) => e.stopPropagation()}>
            <button className="legal-modal-close" onClick={() => setShowTermsModal(false)}>
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
            <div className="legal-modal-content">
              <h1 className="heading-1" style={{ marginBottom: '8px' }}>Heirloomly – Terms of Use</h1>
              <p className="body-small" style={{ color: 'var(--text-light)', marginBottom: '32px' }}>Effective Date: January 2026</p>
              
              <div className="legal-section">
                <p className="body-regular" style={{ lineHeight: '1.7', marginBottom: '24px' }}>
                  Welcome to Heirloomly. These Terms of Use explain how our service works and what you can expect when you use it. We've written them to be clear and human—not complicated or overwhelming.
                </p>
                <p className="body-regular" style={{ fontWeight: 500 }}>
                  By using Heirloomly, you agree to these terms.
                </p>
              </div>

              <div className="legal-section">
                <h2 className="heading-2 space-top-lg">1. What Heirloomly Is</h2>
                <p className="body-regular space-top-sm" style={{ lineHeight: '1.7' }}>
                  Heirloomly is a digital organization and documentation tool designed to help you gather, store, and reflect on important information, memories, and wishes.
                </p>
                <p className="body-regular space-top-md" style={{ fontWeight: 500 }}>Heirloomly is not:</p>
                <ul className="legal-list space-top-sm">
                  <li>A law firm</li>
                  <li>A financial service</li>
                  <li>A medical service</li>
                  <li>A substitute for professional advice</li>
                </ul>
                <p className="body-regular space-top-md" style={{ lineHeight: '1.7' }}>
                  We help you organize and document, so you can be better prepared when working with professionals if you choose to do so.
                </p>
              </div>

              <div className="legal-section">
                <h2 className="heading-2 space-top-lg">2. Using Heirloomly</h2>
                <p className="body-regular space-top-sm" style={{ fontWeight: 500 }}>You agree to use Heirloomly:</p>
                <ul className="legal-list space-top-sm">
                  <li>For lawful purposes</li>
                  <li>For your own personal or family use</li>
                  <li>In a way that respects others and their privacy</li>
                </ul>
                <p className="body-regular space-top-md" style={{ fontWeight: 500 }}>You are responsible for:</p>
                <ul className="legal-list space-top-sm">
                  <li>The accuracy of the information you choose to enter</li>
                  <li>Deciding what to share and with whom</li>
                  <li>Keeping your login credentials secure</li>
                </ul>
                <p className="body-regular space-top-md" style={{ lineHeight: '1.7', fontStyle: 'italic' }}>
                  You are never required to complete everything at once. Heirloomly is designed to be used at your own pace.
                </p>
              </div>

              <div className="legal-section">
                <h2 className="heading-2 space-top-lg">3. Your Information Belongs to You</h2>
                <p className="body-regular space-top-sm" style={{ lineHeight: '1.7', fontWeight: 500 }}>
                  Everything you add to Heirloomly is yours.
                </p>
                <ul className="legal-list space-top-sm">
                  <li>You retain ownership of your content</li>
                  <li>You can update or delete your information at any time</li>
                  <li>We do not sell your data</li>
                  <li>We do not share your data except as needed to provide the service or as required by law</li>
                </ul>
                <p className="body-regular space-top-md" style={{ lineHeight: '1.7' }}>
                  Our role is to store and protect what you choose to add—not to judge, interpret, or advise.
                </p>
              </div>

              <div className="legal-section">
                <h2 className="heading-2 space-top-lg">4. Privacy & Security</h2>
                <p className="body-regular space-top-sm" style={{ lineHeight: '1.7' }}>
                  We use industry-standard security measures to protect your information. While no digital service can guarantee absolute security, protecting your privacy is a core priority.
                </p>
                <p className="body-regular space-top-md" style={{ lineHeight: '1.7' }}>
                  For more details, please review our{' '}
                  <button 
                    onClick={() => {
                      setShowTermsModal(false);
                      setShowPrivacyModal(true);
                    }}
                    className="legal-link"
                  >
                    Privacy Policy
                  </button>.
                </p>
              </div>

              <div className="legal-section">
                <h2 className="heading-2 space-top-lg">5. Not Legal, Financial, or Medical Advice</h2>
                <p className="body-regular space-top-sm" style={{ lineHeight: '1.7', fontWeight: 500 }}>
                  Heirloomly does not provide legal, financial, or medical advice.
                </p>
                <p className="body-regular space-top-sm" style={{ lineHeight: '1.7' }}>
                  Any information you enter, store, or view within Heirloomly is for organizational and personal reference purposes only.
                </p>
                <p className="body-regular space-top-md" style={{ lineHeight: '1.7' }}>
                  If you need professional advice, we encourage you to consult a qualified attorney, financial advisor, or healthcare professional.
                </p>
              </div>

              <div className="legal-section">
                <h2 className="heading-2 space-top-lg">6. Availability & Changes</h2>
                <p className="body-regular space-top-sm" style={{ lineHeight: '1.7' }}>
                  We work hard to keep Heirloomly reliable and accessible, but we may:
                </p>
                <ul className="legal-list space-top-sm">
                  <li>Update features</li>
                  <li>Improve functionality</li>
                  <li>Pause or modify parts of the service</li>
                </ul>
                <p className="body-regular space-top-md" style={{ lineHeight: '1.7' }}>
                  We'll always aim to make changes thoughtfully and with care.
                </p>
              </div>

              <div className="legal-section">
                <h2 className="heading-2 space-top-lg">7. Ending Your Use</h2>
                <p className="body-regular space-top-sm" style={{ lineHeight: '1.7' }}>
                  You may stop using Heirloomly at any time.
                </p>
                <p className="body-regular space-top-sm" style={{ fontWeight: 500 }}>You can:</p>
                <ul className="legal-list space-top-sm">
                  <li>Delete your account</li>
                  <li>Remove your information</li>
                  <li>Export your data (where available)</li>
                </ul>
                <p className="body-regular space-top-md" style={{ lineHeight: '1.7' }}>
                  If you choose to leave, your information remains yours.
                </p>
              </div>

              <div className="legal-section">
                <h2 className="heading-2 space-top-lg">8. Limitation of Responsibility</h2>
                <p className="body-regular space-top-sm" style={{ lineHeight: '1.7' }}>
                  Heirloomly is provided "as is."
                </p>
                <p className="body-regular space-top-sm" style={{ fontWeight: 500 }}>We are not responsible for:</p>
                <ul className="legal-list space-top-sm">
                  <li>Decisions made based on information you store</li>
                  <li>Outcomes related to legal, financial, or medical matters</li>
                  <li>Losses resulting from reliance on the service for professional advice</li>
                </ul>
                <p className="body-regular space-top-md" style={{ lineHeight: '1.7' }}>
                  Our goal is clarity and support—not control or decision-making.
                </p>
              </div>

              <div className="legal-section">
                <h2 className="heading-2 space-top-lg">9. Updates to These Terms</h2>
                <p className="body-regular space-top-sm" style={{ lineHeight: '1.7' }}>
                  If we update these Terms, we'll do so thoughtfully. Continued use of Heirloomly means you accept the updated terms.
                </p>
              </div>

              <div className="legal-section">
                <h2 className="heading-2 space-top-lg">10. Contact Us</h2>
                <p className="body-regular space-top-sm" style={{ lineHeight: '1.7' }}>
                  If you have questions or concerns, we're here to listen.
                </p>
                <p className="body-regular space-top-sm" style={{ lineHeight: '1.7' }}>
                  Contact: <a href="mailto:support@heirloomly.app" className="legal-link">support@heirloomly.app</a>
                </p>
              </div>

              <div className="legal-closing">
                <h3 className="heading-3" style={{ marginBottom: '12px' }}>Closing Note</h3>
                <p className="body-regular" style={{ lineHeight: '1.7' }}>
                  Heirloomly exists to make things feel lighter, not heavier. These terms are meant to protect both you and us—without pressure, fear, or fine print surprises.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Privacy Modal - Placeholder for now */}
      {showPrivacyModal && (
        <div className="legal-modal-overlay" onClick={() => setShowPrivacyModal(false)}>
          <div className="legal-modal" onClick={(e) => e.stopPropagation()}>
            <button className="legal-modal-close" onClick={() => setShowPrivacyModal(false)}>
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
            <div className="legal-modal-content">
              <h1 className="heading-1" style={{ marginBottom: '8px' }}>Heirloomly – Privacy Policy</h1>
              <p className="body-small" style={{ color: 'var(--text-light)', marginBottom: '32px' }}>Effective Date: January 2026</p>
              
              <div className="legal-section">
                <p className="body-regular" style={{ lineHeight: '1.7', marginBottom: '16px' }}>
                  Your privacy matters deeply to us. This policy explains how Heirloomly collects, uses, and protects your information—clearly, simply, and without fine print surprises.
                </p>
              </div>

              <div className="legal-section">
                <h2 className="heading-2 space-top-lg">1. Our Privacy Promise</h2>
                <p className="body-regular space-top-sm" style={{ lineHeight: '1.7', fontWeight: 500 }}>
                  At Heirloomly, we believe:
                </p>
                <ul className="legal-list space-top-sm">
                  <li>Your information belongs to you</li>
                  <li>Your pace should be respected</li>
                  <li>Your data should be protected</li>
                  <li>Your trust should never be taken for granted</li>
                </ul>
                <p className="body-regular space-top-md" style={{ lineHeight: '1.7' }}>
                  We design our systems with care, discretion, and respect.
                </p>
              </div>

              <div className="legal-section">
                <h2 className="heading-2 space-top-lg">2. Information We Collect</h2>
                <p className="body-regular space-top-sm" style={{ lineHeight: '1.7', fontWeight: 500 }}>
                  We collect only what's needed to provide the service.
                </p>
                <p className="body-regular space-top-md" style={{ fontWeight: 500 }}>Information you choose to provide:</p>
                <ul className="legal-list space-top-sm">
                  <li>Email address (for account access and updates)</li>
                  <li>Information, documents, notes, or content you voluntarily add</li>
                  <li>Preferences about sharing or access</li>
                </ul>
                <p className="body-regular space-top-md" style={{ lineHeight: '1.7', fontStyle: 'italic' }}>
                  You decide what to add and when.
                </p>
              </div>

              <div className="legal-section">
                <h2 className="heading-2 space-top-lg">3. How We Use Your Information</h2>
                <p className="body-regular space-top-sm" style={{ fontWeight: 500 }}>We use your information to:</p>
                <ul className="legal-list space-top-sm">
                  <li>Provide and improve the Heirloomly service</li>
                  <li>Store and display your content securely</li>
                  <li>Communicate with you about your account or updates</li>
                  <li>Maintain security and prevent misuse</li>
                </ul>
                <p className="body-regular space-top-md" style={{ fontWeight: 500 }}>We do not use your information for:</p>
                <ul className="legal-list space-top-sm">
                  <li>Advertising</li>
                  <li>Selling data</li>
                  <li>Sharing with third parties for marketing</li>
                </ul>
              </div>

              <div className="legal-section">
                <h2 className="heading-2 space-top-lg">4. Your Information Is Yours</h2>
                <ul className="legal-list space-top-sm">
                  <li>You retain ownership of everything you add to Heirloomly</li>
                  <li>You can edit or delete your information at any time</li>
                  <li>You can request deletion of your account and associated data</li>
                </ul>
                <p className="body-regular space-top-md" style={{ lineHeight: '1.7', fontWeight: 500 }}>
                  We do not claim ownership over your content.
                </p>
              </div>

              <div className="legal-section">
                <h2 className="heading-2 space-top-lg">5. Sharing of Information</h2>
                <p className="body-regular space-top-sm" style={{ lineHeight: '1.7', fontWeight: 500 }}>
                  We do not sell your personal information.
                </p>
                <p className="body-regular space-top-sm" style={{ fontWeight: 500 }}>We may share limited information only when:</p>
                <ul className="legal-list space-top-sm">
                  <li>Required to operate the service (for example, secure hosting)</li>
                  <li>Required by law</li>
                  <li>Necessary to protect the safety or rights of users or the service</li>
                </ul>
                <p className="body-regular space-top-md" style={{ lineHeight: '1.7' }}>
                  Any partners or service providers we use are required to protect your data.
                </p>
              </div>

              <div className="legal-section">
                <h2 className="heading-2 space-top-lg">6. Security</h2>
                <p className="body-regular space-top-sm" style={{ lineHeight: '1.7' }}>
                  We use industry-standard safeguards to protect your information, including encryption and secure storage.
                </p>
                <p className="body-regular space-top-md" style={{ lineHeight: '1.7' }}>
                  While no digital system can guarantee absolute security, protecting your data is a core priority—and we take that responsibility seriously.
                </p>
              </div>

              <div className="legal-section">
                <h2 className="heading-2 space-top-lg">7. Your Choices & Control</h2>
                <p className="body-regular space-top-sm" style={{ lineHeight: '1.7', fontWeight: 500 }}>
                  You are always in control.
                </p>
                <p className="body-regular space-top-sm" style={{ fontWeight: 500 }}>You can:</p>
                <ul className="legal-list space-top-sm">
                  <li>Decide what information to add</li>
                  <li>Decide who can access it (where applicable)</li>
                  <li>Update or remove your data</li>
                  <li>Delete your account entirely</li>
                </ul>
                <p className="body-regular space-top-md" style={{ lineHeight: '1.7', fontStyle: 'italic' }}>
                  There is no pressure to complete everything at once—or at all.
                </p>
              </div>

              <div className="legal-section">
                <h2 className="heading-2 space-top-lg">8. Cookies & Analytics (Light Use)</h2>
                <p className="body-regular space-top-sm" style={{ fontWeight: 500 }}>We may use minimal analytics or cookies to:</p>
                <ul className="legal-list space-top-sm">
                  <li>Understand how the site is used</li>
                  <li>Improve performance and usability</li>
                </ul>
                <p className="body-regular space-top-md" style={{ lineHeight: '1.7' }}>
                  We do not use tracking for advertising purposes.
                </p>
              </div>

              <div className="legal-section">
                <h2 className="heading-2 space-top-lg">9. Children's Privacy</h2>
                <p className="body-regular space-top-sm" style={{ lineHeight: '1.7' }}>
                  Heirloomly is not intended for use by children under 13 without adult involvement. We do not knowingly collect personal information from children without consent.
                </p>
              </div>

              <div className="legal-section">
                <h2 className="heading-2 space-top-lg">10. Changes to This Policy</h2>
                <p className="body-regular space-top-sm" style={{ lineHeight: '1.7' }}>
                  If we update this Privacy Policy, we'll do so thoughtfully and transparently. Continued use of Heirloomly means you accept the updated policy.
                </p>
              </div>

              <div className="legal-section">
                <h2 className="heading-2 space-top-lg">11. Contact Us</h2>
                <p className="body-regular space-top-sm" style={{ lineHeight: '1.7' }}>
                  If you have questions, concerns, or requests related to privacy, we're here.
                </p>
                <p className="body-regular space-top-sm" style={{ lineHeight: '1.7' }}>
                  Email: <a href="mailto:support@heirloomly.app" className="legal-link">support@heirloomly.app</a>
                </p>
              </div>

              <div className="legal-closing">
                <h3 className="heading-3" style={{ marginBottom: '12px' }}>A Final Note</h3>
                <p className="body-regular" style={{ lineHeight: '1.7', marginBottom: '12px' }}>
                  Privacy isn't just a feature—it's part of our values.
                </p>
                <p className="body-regular" style={{ lineHeight: '1.7' }}>
                  Heirloomly exists to help you feel organized, prepared, and at ease. That starts with respecting your information as if it were our own.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
